"use client";

import { useMemo } from "react";

import { WalletContext } from "@/lib/wallet/context";
import { useClaimGameIdentity } from "@/lib/wallet/use-claim-game-identity";
import { useEvmWallet } from "@/lib/wallet/use-evm-wallet";
import { useIdentifyWallet } from "@/lib/wallet/use-identify-wallet";
import { usePhantomWallet } from "@/lib/wallet/use-phantom-wallet";
import { useSolanaSignIn } from "@/lib/wallet/use-solana-sign-in";
import { trackedConnect } from "@/lib/wallet/wallet-telemetry";

import type { WalletState } from "@/lib/wallet/context";
import type { ReactNode } from "react";

/**
 * Both stacks joined for a Phantom build, publishing the same `WalletState`
 * the Glyph bridge publishes. Must sit inside Wagmi and RainbowKit; there is
 * no Solana provider to sit inside, because the adapter is an object this
 * owns rather than a context.
 *
 * `solanaReady` is deliberately absent (optional on `WalletState`): it exists
 * to disable the connect button while Privy boots. There is nothing to boot
 * here, so leaving it undefined is accurate where a hardcoded `true` would be
 * a claim about a state that cannot occur.
 *
 * `claimGameIdentity` names the game seat after the Phantom wallet
 * (`useClaimGameIdentity`).
 */
export function PhantomWalletBridge({
  children,
  returning = false,
}: {
  children: ReactNode;
  returning?: boolean;
}) {
  const evm = useEvmWallet();
  const solana = usePhantomWallet(useSolanaSignIn(), returning);
  useIdentifyWallet(solana.solanaAddress, solana.solana);
  const { solanaAddress, proveWallet } = solana;
  const claimGameIdentity = useClaimGameIdentity(solanaAddress, proveWallet);
  const value = useMemo(
    (): WalletState => ({
      // Holding assets is what "connected" means, and those live in the
      // Solana wallet.
      connected: Boolean(solana.solanaAddress),
      reattaching: solana.reattaching ?? false,
      setConnected: () => undefined,
      evmAddress: evm.evmAddress,
      ...(evm.evmWalletName ? { evmWalletName: evm.evmWalletName } : {}),
      solanaAddress: solana.solanaAddress,
      ...(solana.solanaAddress && solana.walletName
        ? { solanaWalletName: solana.walletName }
        : {}),
      ethereum: evm.ethereum,
      solana: solana.solana,
      connectEvm: trackedConnect("evm", evm.connectEvm),
      addEvmNetwork: evm.addEvmNetwork,
      // Named for the wallet actually used, so Faro tells Phantom from a
      // Wallet Standard wallet such as Coinbase Wallet.
      connectSolana: trackedConnect(
        solana.walletName && solana.walletName !== "Phantom"
          ? solana.walletName.toLowerCase().replace(/\W+/g, "_")
          : "phantom",
        solana.connectSolana
      ),
      // Logging in means the Solana wallet, so logging out does too. ApeChain
      // is dropped from the claim page, the only place it is connected.
      disconnect: solana.disconnectSolana,
      disconnectEvm: evm.disconnectEvm,
      changeEvm: evm.changeEvm,
      claimGameIdentity,
      ...(proveWallet ? { proveWallet } : {}),
    }),
    [evm, solana, claimGameIdentity, proveWallet]
  );
  return <WalletContext value={value}>{children}</WalletContext>;
}
